import React, { useState } from 'react';
import { formatTime, linkifyText, linkifyTextCommunity } from '../utils/utils';
import AudioPlayer from './AudioPlayer';
import MessageReactions from './MessageReactions';
import { QuotedMessage } from './ReplyQuotedMessage';
import { MessageStatus, GroupMessageStatus } from './MessageStatus';
import { LuVideo as FiVideo } from "react-icons/lu";
import { LuPhone as BsTelephone } from "react-icons/lu";
import { formatDuration } from '../utils/chatUtils';
import { IMAGES } from '../constants/images';
import VideoModal from './VideoModal';
import { ROUTES } from '../App';
import useMoveTo from '../constants/navigation';
import ForwardedMessage from './ForwardedMessage';
import SharedJobCard from './SharedJobCard';

const MessageBubble = ({
    message,
    isFromCurrentUser,
    currentUserId,
    senderName,
    senderAvatar,
    showHeader,
    onAvatarClick,
    onImageClick,
    selectionMode,
    selected,
    onToggleReaction,
    onReply,
    onEdit,
    onDelete,
    showReplyButton = false,
    isHost = false,
    chatType = 'private',
    totalMembers,
    loadedImages,
    onImageLoad,
    renderCustomContent
}) => {
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [videoThumbnailLoaded, setVideoThumbnailLoaded] = useState({});
    const [videoThumbnails, setVideoThumbnails] = useState({});
    const navigate = useMoveTo();

    const shouldShowHeader =
        showHeader;

    const shouldShowMissedCallInGroup =
        chatType === 'group' &&
        message.type === 'call' &&
        message.callStatus === 'missed' &&
        message.missedReceiver &&
        message.missedReceiver[currentUserId];

    const shouldRenderCallMessage =
        message.type === 'call' &&
        message.callStatus === 'missed' &&
        (
            chatType === 'private' ||
            shouldShowMissedCallInGroup
        );

    const renderMessageStatus = () => {
        if (chatType === 'private') {
            return <MessageStatus message={message} isFromCurrentUser={isFromCurrentUser} />;
        } else {
            return (
                <GroupMessageStatus
                    message={message}
                    isFromCurrentUser={isFromCurrentUser}
                    totalMembers={totalMembers}
                />
            );
        }
    };

    // Define getVideoThumbnail before it's used
    const getVideoThumbnail = (videoUrl, messageId) => {
        // If we already have a thumbnail for this video, return it
        if (videoThumbnails[messageId]) {
            return videoThumbnails[messageId];
        }

        // If the message already has a thumbnail from the server, use it
        if (message.videoThumbnail) {
            return message.videoThumbnail;
        }

        // Otherwise, generate a thumbnail on the client side
        const video = document.createElement('video');
        video.src = videoUrl;
        video.crossOrigin = 'anonymous';
        video.preload = 'metadata';

        video.addEventListener('loadeddata', () => {
            video.currentTime = 1; // Seek to 1 second
        });

        video.addEventListener('seeked', () => {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const thumbnail = canvas.toDataURL('image/jpeg');

            // Store the generated thumbnail
            setVideoThumbnails(prev => ({ ...prev, [messageId]: thumbnail }));

            // Mark as loaded
            handleVideoThumbnailLoad(messageId);
        });

        video.addEventListener('error', () => {
            // Use fallback image on error
            setVideoThumbnails(prev => ({ ...prev, [messageId]: IMAGES.statues }));
            handleVideoThumbnailLoad(messageId);
        });

        // Return a placeholder immediately, the actual thumbnail will be set when generated
        return videoThumbnails[messageId] || null;
    };


    const handleVideoClick = (videoUrl) => {
        setSelectedVideo(videoUrl);
    };

    const handleVideoThumbnailLoad = (id) => {
        setVideoThumbnailLoaded(prev => ({ ...prev, [id]: true }));
    };

    return (
        <div className={`flex items-start gap-2 mt-3 ${isFromCurrentUser ? "justify-end" : "justify-start"} w-full`}>
            {/* Avatar - fixed width, no shrink */}
            {!isFromCurrentUser && shouldShowHeader ? (
                <button
                    onClick={onAvatarClick}
                >
                    <img
                        src={senderAvatar}
                        alt={senderName}
                        className="w-10 h-10 rounded-full cursor-pointer flex-shrink-0"
                    />
                </button>
            ) : (
                <div className="w-10 flex-shrink-0" />
            )}

            {/* Message Content - takes remaining width, no overflow */}
            <div className={`md:max-w-[75%] max-w-[81%] ${isFromCurrentUser ? "items-end" : "items-start"} ${isFromCurrentUser ? "text-left" : "text-left"}`}>
                {/* Sender Name */}
                {!isFromCurrentUser && shouldShowHeader && (
                    <button
                        onClick={onAvatarClick}
                    >
                        <p
                            className={`font-bold text-[14px] cursor-pointer truncate max-w-full ${message.isHost ? "dark:glow-text-dark glow-text-light" : "text-white"
                                }`}

                        >
                            {senderName}
                        </p>
                    </button>
                )}

                {/* Quoted Message */}
                {message.replyTo && (
                    <div className="max-w-full">
                        <QuotedMessage
                            replyTo={message.replyTo}
                            isFromCurrentUser={isFromCurrentUser}
                            currentUserId={currentUserId}
                            senderName={message.replyTo.senderId === currentUserId ? "You" : senderName}
                        />
                    </div>
                )}

                {/* Forwarded Message */}
                {message.type === 'forwarded' && (message.originalGroup || message.originalCommunity) && (
                    <ForwardedMessage
                        message={message}
                        isFromCurrentUser={isFromCurrentUser}
                        type={message.originalGroup ? 'group' : 'community'}
                        selectionMode={selectionMode}
                        selected={selected}
                        onToggleReaction={onToggleReaction}
                    />
                )}

                {/* Text Message */}
                {message.text && message.type !== 'forwarded' && (
                    <div
                        className={`relative px-3 py-2 ${isFromCurrentUser
                            ? "bg-primary text-white rounded-xl rounded-bl-xl mt-2"
                            : "bg-[#F2F7FB] text-black rounded-b-xl rounded-tr-xl mt-2"
                            } max-w-full break-words ${selectionMode && selected ? "border border-[#57a1be]" : ""
                            }`}
                        style={{
                            wordBreak: "break-word",
                            overflowWrap: "break-word",

                        }}
                    >
                        <div className="w-full">
                            <SharedJobCard message={message} />
                            <p
                                className="whitespace-pre-wrap break-words"
                                style={{
                                    wordBreak: "break-word",
                                    overflowWrap: "anywhere",
                                }}
                            >
                                {chatType === "community" ?
                                    linkifyTextCommunity(
                                        message.text,
                                        message.mentions,
                                        isFromCurrentUser,
                                        (name, uid) => navigate(`${ROUTES.VIEW_PROFILE}/${uid}`)
                                    ) : linkifyText(message.text, isFromCurrentUser)
                                }
                            </p>
                        </div>
                        <div className="flex items-center justify-end gap-1 mt-1 flex-wrap">
                            {message.edited && (
                                <span className="text-xs text-gray-400 italic">(edited)</span>
                            )}
                            <p className="text-xs text-gray-400 whitespace-nowrap">
                                {formatTime(message.timestamp)}
                            </p>
                            {renderMessageStatus()}
                        </div>
                    </div>
                )}

                {/* Image Message */}
                {message.imageUrl && (
                    <div
                        className={`relative mt-2 cursor-pointer max-w-full ${isFromCurrentUser ? "ml-auto" : "mr-auto"}`}
                        style={{ touchAction: "pan-y" }}
                        onClick={onImageClick}
                    >
                        <div
                            className={`
                                relative 
                                w-full max-w-[250px] sm:max-w-[280px]
                                 md:max-w-[320px] aspect-[4/5]
                                  rounded-xl sm:rounded-2xl border-2
                 ${selectionMode && selected ? "border-[#57a1be]" : "border-primary"}
                  overflow-hidden bg-gray-200 dark:bg-[#2A2A2A]
                  ${isFromCurrentUser ? "ml-auto" : "mr-auto"}
                `}
                        >
                            {/* Loading placeholder - matches image dimensions once loaded */}
                            {!loadedImages && (
                                <div className="
                                                                    min-w-[320px]
                                                                    sm:max-w-[320px]
                                                                    sm:max-h-[400px]
                                                                    max-h-[320px]
                                                                    min-h-[320px]
                                                                    sm:min-h-[400px]
                                                                    animate-pulse bg-gray-300 dark:bg-gray-700" />
                            )}

                            {/* Actual image */}
                            <img
                                src={message.imageUrl}
                                alt="Uploaded content"
                                loading="lazy"
                                decoding="async"
                                onLoad={onImageLoad}
                                onError={onImageLoad} // Also hide placeholder on error
                                className={`
                    w-full h-full object-cover transition-opacity duration-300
                    ${loadedImages ? "opacity-100" : "opacity-0 absolute inset-0"}
                    
                `}
                            />
                        </div>

                        {/* Timestamp and status */}
                        <div className="flex items-center justify-end gap-1 mt-1">
                            <p className={`text-xs text-gray-400 ${isFromCurrentUser ? "text-right" : "text-left"}`}>
                                {formatTime(message.timestamp)}
                            </p>
                            {renderMessageStatus()}
                        </div>

                        {/* Reactions for image messages */}
                        {message.reactions && Object.keys(message.reactions).length > 0 && (
                            <div className="mt-2">
                                <MessageReactions
                                    message={message}
                                    currentUserId={currentUserId}
                                    onToggleReaction={onToggleReaction}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Video message with thumbnail and play button */}
                {message.videoUrl && (
                    <div className={`relative mt-1 sm:mt-2 max-w-[full] ${isFromCurrentUser ? "ml-auto" : "mr-auto"} `}>
                        <div
                            className={`relative rounded-xl overflow-hidden cursor-pointer group ${selectionMode && selected ? "border-2 border-[#57a1be]" : ""
                                }`}
                            onClick={() => handleVideoClick(message.videoUrl)}
                        >
                            {/* Video thumbnail */}
                            <div className="relative w-full min-w-[230px] md:min-w-[320px] h-[140px] md:h-[200px] bg-gray-200 dark:bg-[#2A2A2A]">                                {/* Loading shimmer */}
                                {!videoThumbnailLoaded[message.id] && (
                                    <div className="absolute inset-0 animate-pulse bg-gray-300 dark:bg-gray-700 rounded-xl" />
                                )}

                                {/* Thumbnail image */}
                                <img
                                    src={getVideoThumbnail(message.videoUrl, message.id)}
                                    alt="Video thumbnail"
                                    loading="lazy"
                                    decoding="async"
                                    onLoad={() => handleVideoThumbnailLoad(message.id)}
                                    onError={(e) => {
                                        e.target.src = IMAGES.videoPlaceHolder;
                                        handleVideoThumbnailLoad(message.id);
                                    }}
                                    className={`
                                        w-full h-full object-cover transition-opacity duration-300
                                        ${videoThumbnailLoaded[message.id] ? "opacity-100" : "opacity-0"}
                                    `}
                                />

                                {/* Play button overlay */}
                                <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                                    <div className="w-12 h-12 rounded-full bg-white/70 flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
                                        <svg
                                            className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-primary ml-1"
                                            fill="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path d="M8 5v14l11-7z" />
                                        </svg>
                                    </div>
                                </div>

                            </div>
                        </div>

                        {/* Timestamp */}
                        <p className="text-xs text-gray-400 mt-1 text-right">
                            {formatTime(message.timestamp)}
                        </p>

                        {/* Reactions for video messages */}
                        {message.reactions && Object.keys(message.reactions).length > 0 && (
                            <div className="mt-2">
                                <MessageReactions
                                    message={message}
                                    currentUserId={currentUserId}
                                    onToggleReaction={onToggleReaction}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Audio Message */}
                {message.audioUrl && (
                    <div className="relative mt-2 max-w-full">
                        <div className={`${selectionMode && selected ? "border-2 border-[#57a1be] rounded-[0.90rem]" : ""
                            }`}>
                            <AudioPlayer
                                src={message.audioUrl}
                                isFromCurrentUser={isFromCurrentUser}
                            />
                        </div>
                        <div className="flex items-center justify-end gap-1 mt-1">
                            <p className={`text-xs mt-1 ${isFromCurrentUser ? "text-gray-300" : "text-gray-500"
                                } text-right`}>
                                {formatTime(message.timestamp)}
                            </p>
                            {renderMessageStatus()}
                        </div>

                        {/* Reactions for audio messages */}
                        {message.reactions && Object.keys(message.reactions).length > 0 && (
                            <div className="mt-2">
                                <MessageReactions
                                    message={message}
                                    currentUserId={currentUserId}
                                    onToggleReaction={onToggleReaction}
                                />
                            </div>
                        )}
                    </div>
                )}

                {shouldRenderCallMessage ? (
                    <div className={`flex ${isFromCurrentUser ? 'justify-end' : 'justify-start'} mb-2`}>
                        <div className={`max-w-[100%] rounded-lg p-3 ${isFromCurrentUser
                            ? 'bg-primary text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-black dark:text-white'
                            }`}>
                            <div className="flex items-center gap-2">
                                {/* Call icon based on type and status */}
                                {message.callType === 'video' ? (
                                    <FiVideo className="w-5 h-5" />
                                ) : (
                                    <BsTelephone className="w-5 h-5" />
                                )}

                                <div className="flex flex-col">
                                    <span className="font-medium">
                                        {message.callStatus === 'missed' && 'Missed '}
                                        {message.callType === 'video' ? 'Video Call' : 'Voice Call'}
                                    </span>

                                    {/* Show status for other calls */}
                                    {/* {message.callStatus && (
                                        <span className="text-xs opacity-75">
                                            {message.callStatus === 'missed' ? message.senderId === currentUserId ? 'Voice call' : 'You missed a call' :
                                                null
                                            }
                                        </span>
                                    )} */}
                                    <p className={`text-xs mt-1 ${isFromCurrentUser ? "text-gray-300" : "text-gray-500"
                                        } text-right`}>
                                        {formatTime(message.timestamp)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}

                {/* Custom content (polls, etc.) */}
                {renderCustomContent && renderCustomContent(message)}

                {/* Reactions for text messages - placed here with normal margin */}
                {message.text && message.reactions && Object.keys(message.reactions).length > 0 && (
                    <div className="mt-1">
                        <MessageReactions
                            message={message}
                            currentUserId={currentUserId}
                            onToggleReaction={onToggleReaction}
                        />
                    </div>
                )}

                {/* Video Modal */}
                {selectedVideo && (
                    <VideoModal
                        videoUrl={selectedVideo}
                        onClose={() => setSelectedVideo(null)}
                    />
                )}
            </div>
        </div>
    );
};

export default React.memo(MessageBubble);
